import { getPulidKind, PulidKind } from '@/utils/pulid';

/**
 * The chat agent emits internal links as ordinary markdown links on a custom
 * luminary:// scheme, e.g. [Jones SLAT](luminary://record/entity_01HZX...) or
 * [People page](luminary://page/people). The vocabulary (scheme, page keys,
 * routable record prefixes) mirrors the backend contract in platform
 * server/pkg/chatmarkdown/internallink.go; when the backend vocabulary grows,
 * only the maps in this file should need to change.
 *
 * This module is deliberately navigation-free (it imports only PULID helpers)
 * so the markdown transformer and node can be registered with the core editor
 * without pulling the route table — and therefore the chat page — into an
 * import cycle. Route resolution lives in `internalLinkPath.ts`, which only the
 * (lazily loaded) renderer imports.
 */
export const INTERNAL_LINK_SCHEME = 'luminary://';

const INTERNAL_LINK_PAGE_KEYS = ['people', 'estate', 'balance-sheet'] as const;
export type InternalLinkPageKey = (typeof INTERNAL_LINK_PAGE_KEYS)[number];

function isInternalLinkPageKey(key: string): key is InternalLinkPageKey {
  return INTERNAL_LINK_PAGE_KEYS.includes(key as InternalLinkPageKey);
}

// The record kinds the backend emits links for. Other PULID kinds in a
// luminary://record/ target are treated as unparseable.
const ROUTABLE_RECORD_KINDS: PulidKind[] = [
  PulidKind.ClientOrganization,
  PulidKind.ClientPresentationV2,
  PulidKind.ClientProfile,
  PulidKind.Entity,
  PulidKind.Memo,
  PulidKind.TestamentaryEntity,
  // Document summaries are deliberately handled in place instead of by
  // navigation: chat surfaces mount an InternalLinkInterceptor that opens the
  // referenced summary in the side content pane beside the transcript. Where
  // no interceptor is mounted, DS_ links degrade to plain text.
  PulidKind.DocumentSummary,
];

export type InternalLinkTarget =
  | { kind: 'record'; recordId: string }
  | { kind: 'page'; pageKey: InternalLinkPageKey };

/**
 * Parses a luminary:// href into a routable target. Returns null for
 * non-luminary hrefs and for malformed/unknown targets (unknown page keys,
 * non-routable record prefixes), which callers should render as plain text.
 */
export function parseInternalLinkHref(href: string): InternalLinkTarget | null {
  if (!href.startsWith(INTERNAL_LINK_SCHEME)) {
    return null;
  }

  const [targetType, ...idParts] = href
    .slice(INTERNAL_LINK_SCHEME.length)
    .split('/');
  const id = idParts.join('/');
  if (!id) {
    return null;
  }

  if (targetType === 'record') {
    const pulidKind = getPulidKind(id);
    if (pulidKind && ROUTABLE_RECORD_KINDS.includes(pulidKind)) {
      return { kind: 'record', recordId: id };
    }
    return null;
  }

  if (targetType === 'page' && isInternalLinkPageKey(id)) {
    return { kind: 'page', pageKey: id };
  }

  return null;
}
